import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, Truck, Shield, Upload, Smartphone, CheckCircle } from 'lucide-react';
import { Button, Card, Input } from '../components/common';
import { useCart } from '../hooks/useCart';
import { useAuthStore } from '../store/useAuthStore';
import { formatCurrency } from '../utils/format';
import { sendOrderWhatsAppNotification } from '../services/api';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { items, subtotal, shipping, tax, total, clearCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [ecocashNumber, setEcocashNumber] = useState('');

  // Initialize shipping info with user data if authenticated
  const [shippingInfo, setShippingInfo] = useState({
    firstName: isAuthenticated && user ? user.firstName : '',
    lastName: isAuthenticated && user ? user.lastName : '',
    email: isAuthenticated && user ? user.email : '',
    phone: isAuthenticated && user ? user.phone : '',
    address: '',
    city: 'Harare',
    zipCode: '',
  });

  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
  });

  const orderNumber = `ORD-${Date.now().toString().slice(-8)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (paymentMethod === 'ecocash' && !paymentProof) {
      alert('Please upload your payment confirmation screenshot or receipt');
      return;
    }
    
    setIsProcessing(true);
    
    // Send WhatsApp notification
    sendOrderWhatsAppNotification(
      orderNumber,
      `${shippingInfo.firstName} ${shippingInfo.lastName}`,
      shippingInfo.phone,
      shippingInfo.email,
      shippingInfo.address,
      shippingInfo.city,
      shippingInfo.zipCode,
      paymentMethod === 'card' ? 'Credit Card' : 
       paymentMethod === 'ecocash' ? 'EcoCash' : 
       paymentMethod === 'paypal' ? 'PayPal' : 'Cash on Delivery',
      items.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
      subtotal,
      shipping,
      tax,
      total
    );
    
    // If paying with EcoCash, handle the payment proof
    if (paymentMethod === 'ecocash' && paymentProof) {
      // In a real app, you would upload to your server here
      console.log('Uploading payment proof:', paymentProof.name);
      console.log('Ecocash number:', ecocashNumber);
      console.log('Order number:', orderNumber);
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // After "upload", proceed to success with payment method
      clearCart();
      navigate('/checkout/success', { state: { paymentMethod, orderNumber } });
    } else {
      // Simulate payment processing for other methods
      setTimeout(() => {
        clearCart();
        navigate('/checkout/success', { state: { paymentMethod, orderNumber } });
        setIsProcessing(false);
      }, 2000);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setShippingInfo({
      ...shippingInfo,
      [name]: value,
    });
  };

  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPaymentInfo({
      ...paymentInfo,
      [name]: value,
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        alert('Please upload a JPG, PNG, or PDF file');
        return;
      }
      
      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setUploadStatus('uploading');
      setPaymentProof(file);
      
      // Simulate upload
      setTimeout(() => {
        setUploadStatus('success');
      }, 1000);
    }
  };

  const handleEcocashWhatsApp = () => {
    const message = encodeURIComponent(
      `New EcoCash Payment - Order #${orderNumber}\n` +
      `Amount: ${formatCurrency(total)}\n` +
      `Customer: ${shippingInfo.firstName} ${shippingInfo.lastName}\n` +
      `Phone: ${shippingInfo.phone}\n` +
      `Ecocash: ${ecocashNumber}\n` +
      `Reference: ${orderNumber}`
    );
    window.open(`https://wa.me/+263773520100?text=${message}`, '_blank');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-dark-dark dark:bg-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-dark-light dark:bg-gray-100 rounded-full flex items-center justify-center">
            <Truck className="w-10 h-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h2 className="text-2xl font-bold text-white dark:text-dark-dark mb-4">
            Your cart is empty
          </h2>
          <p className="text-gray-400 dark:text-gray-500 mb-8">
            Add some items to your cart before checking out.
          </p>
          <Button variant="primary" onClick={() => navigate('/products')}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-white dark:text-dark-dark mb-8 transition-colors duration-300">
          Checkout
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-8">
            {/* Shipping Information */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Truck className="w-6 h-6 text-accent dark:text-primary" />
                <h2 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                  Shipping Information
                </h2>
              </div>
              
              <form className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    name="firstName"
                    value={shippingInfo.firstName}
                    onChange={handleInputChange}
                    required
                  />
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={shippingInfo.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={shippingInfo.email}
                  onChange={handleInputChange}
                  required
                />
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Phone Number"
                    name="phone"
                    value={shippingInfo.phone}
                    onChange={handleInputChange}
                    required
                  />
                  <Input
                    label="ZIP Code"
                    name="zipCode"
                    value={shippingInfo.zipCode}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <Input
                  label="Address"
                  name="address"
                  value={shippingInfo.address}
                  onChange={handleInputChange}
                  required
                />
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white dark:text-gray-700 mb-1.5 transition-colors duration-300">
                      City
                    </label>
                    <select 
                      className="w-full border border-dark-light dark:border-gray-300 rounded-lg px-4 py-2.5 bg-dark dark:bg-white text-white dark:text-dark-dark transition-colors duration-300"
                      value={shippingInfo.city}
                      onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                    >
                      <option>Harare</option>
                      <option>Bulawayo</option>
                      <option>Chitungwiza</option>
                      <option>Mutare</option>
                    </select>
                  </div>
                  <Input
                    label="Delivery Instructions (Optional)"
                    name="instructions"
                    placeholder="e.g., Leave at front door"
                  />
                </div>
              </form>
            </Card>

            {/* Payment Method */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-accent dark:text-primary" />
                <h2 className="text-xl font-semibold text-white dark:text-dark-dark transition-colors duration-300">
                  Payment Method
                </h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {['card', 'ecocash', 'paypal', 'cash'].map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setPaymentMethod(method)}
                    className={`p-4 border rounded-lg text-center transition-colors duration-200 ${
                      paymentMethod === method
                        ? 'border-accent dark:border-primary bg-accent/10 dark:bg-primary/10'
                        : 'border-dark-light dark:border-gray-200 hover:border-accent dark:hover:border-primary'
                    }`}
                  >
                    <div className="font-medium text-white dark:text-dark-dark capitalize transition-colors duration-300">
                      {method === 'card' ? 'Credit Card' : 
                       method === 'ecocash' ? 'EcoCash' : method}
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Card Payment Form */}
              {paymentMethod === 'card' && (
                <form className="space-y-4">
                  <Input
                    label="Card Number"
                    name="cardNumber"
                    value={paymentInfo.cardNumber}
                    onChange={handlePaymentChange}
                    placeholder="1234 5678 9012 3456"
                    required
                  />
                  
                  <Input
                    label="Cardholder Name"
                    name="cardName"
                    value={paymentInfo.cardName}
                    onChange={handlePaymentChange}
                    placeholder="John Doe"
                    required
                  />
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Expiry Date"
                      name="expiry"
                      value={paymentInfo.expiry}
                      onChange={handlePaymentChange}
                      placeholder="MM/YY"
                      required
                    />
                    <Input
                      label="CVV"
                      name="cvv"
                      value={paymentInfo.cvv}
                      onChange={handlePaymentChange}
                      placeholder="123"
                      required
                    />
                  </div>
                </form>
              )}
              
              {/* EcoCash Payment Form */}
              {paymentMethod === 'ecocash' && (
                <div className="space-y-6">
                  {/* EcoCash Instructions */}
{paymentMethod === 'ecocash' && (
  <div className="mt-6">
    <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-3 transition-colors duration-300">
      How to Pay with EcoCash:
    </h3>
    <ol className="list-decimal ml-5 space-y-2 text-gray-300 dark:text-gray-700">
      <li>Go to your EcoCash menu</li>
      <li>Select <strong className="text-white dark:text-dark-dark">"Send Money"</strong></li>
      <li>Enter this number: <strong className="text-white dark:text-dark-dark">+263 77 352 0100</strong></li>
      <li>Amount: <strong className="text-white dark:text-dark-dark">{formatCurrency(total)}</strong></li>
      <li>Reference: <strong className="text-white dark:text-dark-dark">{orderNumber}</strong></li>
      <li>Complete the transaction</li>
    </ol>
  </div>
)}
                  
                  {/* EcoCash Number Input */}
                  <div>
                    <Input
                      label="Your EcoCash Number (for receipt)"
                      type="tel"
                      placeholder="+263 xxx xxx xxx"
                      value={ecocashNumber}
                      onChange={(e) => setEcocashNumber(e.target.value)}
                      required
                    />
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      We'll send payment confirmation to this number
                    </p>
                  </div>
                  
                  {/* Payment Proof Upload */}
                  <div>
                    <label className="block text-sm font-medium text-white dark:text-gray-700 mb-2 transition-colors duration-300">
                      Upload Payment Confirmation
                    </label>
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-dark-light dark:border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                        <p className="text-sm text-gray-400 dark:text-gray-500 mb-2">
                          Upload screenshot of successful payment
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-600 mb-4">
                          Accepts JPG, PNG, or PDF (max 5MB)
                        </p>
                        <input
                          type="file"
                          id="paymentProof"
                          accept="image/*,.pdf"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="paymentProof"
                          className="inline-block px-4 py-2 bg-accent dark:bg-primary text-white rounded-lg hover:opacity-90 cursor-pointer transition-opacity duration-200"
                        >
                          Choose File
                        </label>
                      </div>
                      
                      {/* File Upload Status */}
                      {paymentProof && (
                        <div className={`p-3 rounded-lg flex items-center justify-between ${
                          uploadStatus === 'success' 
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                            : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                        }`}>
                          <div className="flex items-center gap-3">
                            {uploadStatus === 'success' ? (
                              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                            ) : (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            )}
                            <div>
                              <p className="font-medium text-white dark:text-dark-dark">
                                {paymentProof.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {(paymentProof.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          {uploadStatus === 'success' && (
                            <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                              Uploaded
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* WhatsApp Contact Option */}
                      <div className="text-center">
                        <p className="text-sm text-gray-400 dark:text-gray-500 mb-2">
                          Having trouble? Contact us on WhatsApp
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleEcocashWhatsApp}
                          leftIcon={<Smartphone className="w-4 h-4" />}
                        >
                          WhatsApp Support
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* PayPal Payment */}
              {paymentMethod === 'paypal' && (
                <div className="text-center py-8">
                  <p className="text-gray-300 dark:text-gray-600 mb-4 transition-colors duration-300">
                    You will be redirected to PayPal to complete your payment.
                  </p>
                  <Button variant="outline" size="lg">
                    Continue with PayPal
                  </Button>
                </div>
              )}
              
              {/* Cash on Delivery */}
              {paymentMethod === 'cash' && (
                <div className="text-center py-8">
                  <p className="text-gray-300 dark:text-gray-600 transition-colors duration-300">
                    Pay with cash upon delivery. Our delivery agent will collect payment.
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
                Order Summary
              </h2>
              
              {/* Order Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-dark-light dark:bg-gray-100 rounded overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white dark:text-dark-dark text-sm transition-colors duration-300">
                        {item.name}
                      </p>
                      <p className="text-gray-400 dark:text-gray-500 text-xs transition-colors duration-300">
                        {item.quantity} × {formatCurrency(item.price)}
                      </p>
                    </div>
                    <p className="font-medium text-accent dark:text-primary transition-colors duration-300">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Price Breakdown */}
              <div className="space-y-3 border-t border-dark-light dark:border-gray-200 pt-4 mb-6">
                <div className="flex justify-between text-gray-400 dark:text-gray-500 transition-colors duration-300">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-400 dark:text-gray-500 transition-colors duration-300">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? 'text-accent dark:text-primary' : ''}>
                    {shipping === 0 ? 'FREE' : formatCurrency(shipping)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-400 dark:text-gray-500 transition-colors duration-300">
                  <span>Tax</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-white dark:text-dark-dark pt-3 border-t border-dark-light dark:border-gray-200 transition-colors duration-300">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
              
              {/* Security Badge */}
              <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
                <Shield className="w-4 h-4" />
                <span>Secure checkout</span>
                <Lock className="w-4 h-4 ml-auto" />
              </div>
              
              {/* Submit Button */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleSubmit}
                disabled={isProcessing || (paymentMethod === 'ecocash' && !paymentProof)}
                leftIcon={isProcessing ? null : <Lock className="w-5 h-5" />}
              >
                {isProcessing ? 'Processing...' : 
                 paymentMethod === 'ecocash' ? 'Submit Payment Proof' : 
                 `Pay ${formatCurrency(total)}`}
              </Button>
              
              {/* Order Number Display */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Order Number: <span className="font-mono font-medium">{orderNumber}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-600 mt-1">
                  Keep this number for reference
                </p>
              </div>
              
              {/* Terms */}
              <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-4 transition-colors duration-300">
                By completing your purchase, you agree to our{' '}
                <a href="/terms" className="text-accent dark:text-primary hover:underline">
                  Terms of Service
                </a>
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;